import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { filter } from "rxjs";
import { DeviceDetectorService } from "ngx-device-detector";
import { CalculationTodayStoreService } from "@bitwarden/web-vault/app/services/store/calculation-today/calculation-today.store.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { DecimalPipe } from "@angular/common";
import { MacroNumberPipe } from "@bitwarden/web-vault/app/pipes/macro-number.pipe";
import { MatDialog } from "@angular/material/dialog";
import {
  ConfirmationDialogService,
  CtaNotificationData,
} from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { Router } from "@angular/router";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { getDigitCount } from "@bitwarden/web-vault/app/shared/utils/helper-number-format";
import { DialogFactory } from "@vault/web-vault/app/components/dialog/dialogFactory";
import { ScenarioStoreService } from "@bitwarden/web-vault/app/services/store/scenario/scenario.store.service";
import { ScenarioOptionsType } from "@vault/web-vault/app/models/types/scenario-group.types";
import { ScenarioDisplayView } from "@vault/web-vault/app/models/view/scenario/scenario-display.view";
import { ViewReadinessService } from "@bitwarden/web-vault/app/services/view-readiness.service";
import { UpgradeNavigationService } from "@bitwarden/web-vault/app/services/subscription/upgrade-navigation.service";

@Component({
  selector: "app-current-balance",
  templateUrl: "./current-balance.html",
  providers: [DecimalPipe, MacroNumberPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CurrentBalanceComponent implements OnInit {
  private router = inject(Router);
  private upgradeNavigation = inject(UpgradeNavigationService);
  private decimalPipe = inject(DecimalPipe);
  private macroNumberPipe = inject(MacroNumberPipe);
  protected readonly Number = Number;
  protected dialog = inject(MatDialog);
  protected i18n = inject(I18nService);
  protected confirmationDialogService = inject(ConfirmationDialogService);
  protected userStore = inject(UserStoreService);
  protected calculatedStore = inject(CalculationTodayStoreService);
  private viewReadiness = inject(ViewReadinessService);
  protected isViewReady = computed(() => this.viewReadiness.todayViewReady());
  // Skeleton shown until the today calc is actually ready. Deliberately keyed off readiness
  // (not the transient isBackgroundLoading flag): the flag clears in bootstrap's finally, which —
  // on a slow/stuck calc — could clear before readiness and leave the region blank (PR #3708
  // item 16 / Kevin's terminal-@else note). This is exactly !isViewReady(), so
  // @if (isViewReady()) / @else if (showBalanceSkeleton()) is exhaustive — never blank.
  protected showBalanceSkeleton = computed(() => !this.viewReadiness.todayViewReady());
  private dialogFactory: DialogFactory;
  private scenarioStore = inject(ScenarioStoreService);
  private destroyRef = inject(DestroyRef);
  protected isEyeOpen = this.userStore.showBalance;
  tickerPaused = false;
  private deviceDetectorService = inject(DeviceDetectorService);
  private static readonly maxStandardDigits = 11;
  protected moreThanCurrentAccount = signal<number | null>(null);

  isPremium = computed(() => this.userStore?.subscription?.activeSubscription()?.isPremium);
  // Gate the "upgrade to premium" header on resolved status: activeSubscription() is null
  // (isPremium undefined) both while loading and for a free user, so without this the header
  // flashes for bundle-subscribed users while their status is still resolving (#14137).
  protected showUpgradeHeader = computed(
    () => this.userStore?.subscription?.isResolved() && !this.isPremium(),
  );
  getCurrencyCode = computed(
    () => this.userStore?.preferences?.preferenceView()?.coreCurrency || "",
  );

  balance = computed(() => {
    return this.calculatedStore.balanceCurrent.balances() || 0;
  });

  getScenario(scenarioType: ScenarioOptionsType) {
    const scenarioCollection = this.scenarioStore.interestScenario.collection();
    return scenarioCollection?.find(
      (scenario: ScenarioDisplayView) => scenario.scenarioType === scenarioType,
    );
  }

  constructor() {
    this.dialogFactory = new DialogFactory();

    this.scenarioStore.storeState$
      .pipe(
        filter((x) => x.status === "Completed"),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.updateDifference();
      });
  }

  ngOnInit(): void {
    this.updateDifference();
  }

  private updateDifference(): void {
    const best = this.getScenario("bestNewInterestRate")?.finalBalanceAmount;
    const current = this.getScenario("currentInterestRate")?.finalBalanceAmount;
    if (best != null && current != null && best > current) {
      this.moreThanCurrentAccount.set(Number((best - current).toFixed(2)));
    } else {
      this.moreThanCurrentAccount.set(null);
    }
  }

  // Below macroNumber's 1,000 abbreviation threshold it falls back to adding decimal precision
  // (e.g. "12.3") instead of shortening, which can render *wider* than the zero-decimal fullText
  // candidate ("12") and break LargeAmountComponent's assumption that each candidate is no wider
  // than the last — so only hand it values that actually abbreviate; below that, reuse fullText's
  // own zero-decimal formatting so the two candidates are never inconsistent.
  protected moreThanCurrentAccountCompact = computed(() => {
    const value = this.moreThanCurrentAccount();
    if (value === null) {
      return "";
    }
    return Math.abs(value) >= 1000
      ? this.macroNumberPipe.transform(value)
      : (this.decimalPipe.transform(value, "1.0-0") ?? "");
  });

  // Deliberately a separate, older (fixed-threshold + mobile-only) mechanism from
  // LargeAmountComponent's real-width measurement used for `moreThanCurrentAccount` above: this
  // gates the main balance() ticker figure, which is reused inside the rollable/animated title
  // (`rollableWidgetTitle`) via `ngTemplateOutlet` — a context LargeAmountComponent's
  // ResizeObserver-based measurement hasn't been verified against (animated transform, repeated
  // template instances). Left as-is (#5117 scoped to the delta/hero-card figures only); revisit
  // if the main balance figure needs the same width-based fix.
  UseMacroNumber(amount: number | undefined | null): boolean {
    const isMobile = this.deviceDetectorService.isMobile();
    return isMobile && getDigitCount(amount) > CurrentBalanceComponent.maxStandardDigits;
  }

  splitBalance(value: number | undefined) {
    if (value === null || value === undefined) {
      return { sign: "", integer: "", decimals: "" };
    }

    const sign = value < 0 ? "-" : "";
    const abs = Math.abs(value);
    const formatted = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(abs);

    const match = formatted.match(/(.+?)([.,]\d{2})$/);
    if (match) {
      return { sign, integer: match[1], decimals: match[2] };
    }

    return { sign, integer: formatted, decimals: "" };
  }

  balanceParts = computed(() => this.splitBalance(this.balance()));

  protected toggleEye() {
    this.userStore.setShowBalance(!this.userStore.showBalance());
  }

  async openPremiumDialog() {
    const i18nService = this.confirmationDialogService["i18nService"];
    const data: CtaNotificationData = {
      title: i18nService.t("updateBalanceAutomatically"),
      message: i18nService.t("callToPremiumMessageBalance"),
      cancel: { method: () => {}, title: i18nService.t("maybeLater") },
      confirm: {
        method: () => this.upgradeNavigation.goToUpgrade(),
        title: i18nService.t("startFreeTrial"),
      },
      reminder: i18nService.t("cancelPremiumReminder"),
    };
    await this.confirmationDialogService.ctaNotification(data);
  }

  pauseTicker() {
    this.tickerPaused = true;
  }

  resumeTicker() {
    this.tickerPaused = false;
  }

  callScenarioDifferenceDialog() {
    this.dialogFactory.openScenarioDifferenceMessage();
  }
}
